import { PropsWithChildren, ReactNode, useEffect } from 'react';
import Sheet from 'react-modal-sheet';

import { GetComponentProps, Prettify } from '@/types';
import { cn } from '@/utils';

type SheetTypes = GetComponentProps<typeof Sheet>;
type SheetProps = Pick<SheetTypes, 'disableDrag' | 'isOpen' | 'onClose'>;

type Props = Prettify<
  PropsWithChildren<
    {
      title?: string | ReactNode;
      showCloseButton?: boolean;
      closeOutside?: boolean;
      wrapperClassName?: string;
      headerClass?: string;
      disableBodyScroll?: boolean;
      onAfterClose?: () => void;
    } & SheetProps
  >
>;

export const BottomSheet = ({
  isOpen,
  onClose,
  children,
  title = '',
  disableDrag = false,
  showCloseButton = true,
  closeOutside = true,
  wrapperClassName,
  disableBodyScroll,
  headerClass,
  onAfterClose,
}: Props) => {
  const displayTitleOrCloseBtn = showCloseButton || title;

  useEffect(() => {
    if (disableBodyScroll) {
      document.body.classList.toggle('overflow-hidden', isOpen);
    }
  }, [isOpen, disableBodyScroll]);

  return (
    <Sheet
      detent="content-height"
      disableDrag={disableDrag}
      disableScrollLocking={true}
      isOpen={isOpen}
      onClose={onClose}
      onCloseEnd={onAfterClose}
    >
      <Sheet.Container className="!rounded-tl-[20px] !rounded-tr-[20px]">
        {displayTitleOrCloseBtn && (
          <Sheet.Header
            className={cn(
              'flex justify-between py-2 px-3 pt-3 pb-0',
              headerClass
            )}
          >
            {title && (
              <h2 className="text-gdt-gray text-base font-bold lg:hidden flex-1 justify-center text-center mt-7">
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                className="w-[1.5rem] h-[1.5rem] right-5 top-5 items-center justify-center ml-auto"
                data-testid="close-modal-sheet"
                type="button"
                onClick={onClose}
              >
                <svg
                  fill="none"
                  height="24"
                  style={{ transform: 'translate(-12px, 5px)' }}
                  viewBox="0 0 24 24"
                  width="24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    clipRule="evenodd"
                    d="M18.7071 5.29289C19.0976 5.68342 19.0976 6.31658 18.7071 6.70711L6.70711 18.7071C6.31658 19.0976 5.68342 19.0976 5.29289 18.7071C4.90237 18.3166 4.90237 17.6834 5.29289 17.2929L17.2929 5.29289C17.6834 4.90237 18.3166 4.90237 18.7071 5.29289Z"
                    fill="#A09EB3"
                    fillRule="evenodd"
                  />
                  <path
                    clipRule="evenodd"
                    d="M5.29289 5.29289C5.68342 4.90237 6.31658 4.90237 6.70711 5.29289L18.7071 17.2929C19.0976 17.6834 19.0976 18.3166 18.7071 18.7071C18.3166 19.0976 17.6834 19.0976 17.2929 18.7071L5.29289 6.70711C4.90237 6.31658 4.90237 5.68342 5.29289 5.29289Z"
                    fill="#A09EB3"
                    fillRule="evenodd"
                  />
                </svg>
              </button>
            )}
          </Sheet.Header>
        )}
        <Sheet.Content
          className={cn(
            'px-5 pb-4',
            {
              'pt-10': !displayTitleOrCloseBtn,
            },
            wrapperClassName
          )}
        >
          <Sheet.Scroller className="!overflow-visible">
            {children}
          </Sheet.Scroller>
        </Sheet.Content>
      </Sheet.Container>
      <Sheet.Backdrop
        onTap={() => {
          if (!closeOutside) return;

          onClose();
        }}
      />
    </Sheet>
  );
};
